import type { Log } from '@blakeelearning/log'
import type Store from '@ember-data/store'
import Route from '@ember/routing/route'
import { service } from '@ember/service'
import { task } from 'ember-concurrency'
import type { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type WindowService from 'teacher-ui-client/services/window'
import type Student from 'teacher-ui-client/models/student'

export default class LoginCardsRoute extends Route {
  @service store!: Store

  @service window!: WindowService

  @service log!: Log

  queryParams = {
    subscriptionType: {
      replace: true,
    },
  }

  loadStudents = task(async (studentIds: string[]) => {
    const students = await this.store.query<Student>(
      'student',
      { filter: { id: studentIds } },
      { adapterOptions: { usePost: true } },
    )
    // sort by grade, then by first name
    return students.slice().sort((s1, s2) => {
      return s1.gradePosition - s2.gradePosition || s1.firstName.localeCompare(s2.firstName)
    })
  })

  // While we could do an asynchronous model hook and await the students, it is more helpful to not do so, as it allows
  // us to render a loading spinner in the template before attempting to render the (potentially many) login cards,
  // which may take multiple seconds.
  // If you rely on the loader substates, you aren't able to render a spinner in time, and the page remains blank while
  // rendering the login cards.
  model({ subscriptionType }: { subscriptionType?: SubscriptionType }) {
    // Get the ids, then remove them from the local storage (no longer needed)
    const studentIds = this.window.getWindow.localStorage.getItem('teacher-ui-client.login-cards.student-ids')
    this.window.getWindow.localStorage.removeItem('teacher-ui-client.login-cards.student-ids')

    if (subscriptionType && studentIds?.length) {
      return { subscriptionType, studentsLoader: this.loadStudents.perform(studentIds.split(',')) }
    }

    // There's no recovery from here. Page was opened in a new tab, so just close it.
    // Not sure the exact cause of this - could be local storage expired, not sure.
    this.log.warn('no subscriptionType or students defined in login cards route model hook', {
      subscriptionType,
      studentIds,
    })
    window.close()
    return {}
  }
}
